import { colors, fontFamily, fontSize, radius, spacing } from '../styles/theme'

/** Subprocessors, kept in step with docs/security/subprocessors.md. */
const SUBPROCESSORS: [string, string][] = [
  ['Microsoft Azure', 'Hosting, database, file storage, secrets, telemetry (US)'],
  ['Azure Communication Services', 'Transactional email'],
  ['Stripe', 'Payments and billing'],
  ['Anthropic', 'Optional AI features, only when you use them'],
  ['Google, Microsoft, Apple', 'Sign-in, only if you choose it'],
  ['Cloudflare Turnstile', 'Bot protection on sign-in and registration'],
  ['GitHub', 'Source code and build pipeline (no customer data)'],
  ['Google Workspace', 'Company email, including support and security reports'],
]

const PRACTICES: [string, string][] = [
  ['Encryption', 'TLS 1.2+ in transit. Database, files and backups encrypted at rest; stored connector credentials and MFA secrets are additionally encrypted by the application.'],
  ['Access', 'Multi-factor authentication is mandatory for administrators. Role-based permissions per team, quarterly access reviews, and every privileged action recorded.'],
  ['Isolation', 'Every request is scoped to its team; cross-tenant access is tested automatically on every change.'],
  ['Retention', 'Render payloads and outputs are kept 30 days by default (configurable per team). Account data is erased on request with a deletion certificate.'],
  ['Change control', 'Every change goes through a pull request and must pass automated tests, dependency audits and security tests before it can deploy; releases can be rolled back.'],
  ['Monitoring', 'External uptime monitoring, security alerting on sign-in anomalies and privilege changes, and a tamper-evident compliance log.'],
  ['Resilience', 'Point-in-time database backups restored and verified monthly. Recovery objectives: 4 hours to restore service, at most 15 minutes of data loss.'],
  ['Incidents', 'A documented incident-response plan. Affected customers are notified within 72 hours of confirming an incident that affects their data.'],
]

const h2: React.CSSProperties = { fontSize: fontSize.lg, fontWeight: 700, color: colors.text, margin: `${spacing.xl} 0 ${spacing.sm}` }
const p: React.CSSProperties = { color: colors.textSecondary, lineHeight: 1.6, margin: `0 0 ${spacing.sm}` }

/** Public trust page; the Policy link of /.well-known/security.txt. Claims here must match docs/security. */
export default function SecurityPage() {
  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: `${spacing.xl} ${spacing.md}`, fontFamily: fontFamily.sans }}>
      <h1 style={{ fontSize: fontSize.xxl, fontWeight: 800, color: colors.text, marginBottom: spacing.sm }}>Security at DocMorphia</h1>
      <p style={p}>Your templates and data are the product. This page describes how we protect them and how to reach us.</p>

      <h2 style={h2}>Report a vulnerability</h2>
      <p style={p}>
        Email <a href="mailto:security@docmorphia.com" style={{ color: colors.primary }}>security@docmorphia.com</a>.
        We will acknowledge your report and keep you informed while we fix it. Please avoid privacy violations,
        data destruction and service disruption while testing. Our <a href="/.well-known/security.txt" style={{ color: colors.primary }}>security.txt</a> has the details.
      </p>

      <h2 style={h2}>Compliance</h2>
      <p style={p}>
        We operate a SOC 2 control set (security, availability, confidentiality) with continuous automated evidence.
        The independent SOC 2 Type II audit has not yet been performed; this page will say so when a report is available.
      </p>

      <h2 style={h2}>How we protect your data</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: spacing.md }}>
        {PRACTICES.map(([title, body]) => (
          <div key={title} style={{ border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: spacing.md, background: colors.surface }}>
            <div style={{ fontWeight: 700, color: colors.text, marginBottom: spacing.xs }}>{title}</div>
            <div style={{ color: colors.textSecondary, fontSize: fontSize.sm, lineHeight: 1.5 }}>{body}</div>
          </div>
        ))}
      </div>

      <h2 style={h2}>Subprocessors</h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fontSize.sm }}>
          <tbody>
            {SUBPROCESSORS.map(([name, purpose]) => (
              <tr key={name} style={{ borderTop: `1px solid ${colors.border}` }}>
                <td style={{ padding: spacing.sm, fontWeight: 600, color: colors.text, whiteSpace: 'nowrap' }}>{name}</td>
                <td style={{ padding: spacing.sm, color: colors.textSecondary }}>{purpose}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ ...p, marginTop: spacing.sm }}>This list is kept current with our vendor register.</p>
    </div>
  )
}
